import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";
import Modal, { ConfirmDialog } from "@/components/Modal";
import {
  EmptyState,
  ErrorMessage,
  PageHeader,
  Pagination,
  SearchInput,
  StatusBadge,
  StatusFilter,
  Toolbar,
} from "@/components/DataTableParts";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const ENDPOINT = "/warranty-eligibility-rules";

const emptyForm = {
  code: "",
  label: "",
  days_offset: 0,
  priority_weight: 100,
  is_active: true,
};

export default function EligibilityRulesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [isActive, setIsActive] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [toDelete, setToDelete] = useState(null);

  // Debounced search
  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: 10 };
    if (qDebounced) p.q = qDebounced;
    if (isActive !== null) p.is_active = isActive;
    return p;
  }, [page, qDebounced, isActive]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(ENDPOINT, { params });
      setData(r.data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.warn("EligibilityRulesPage.load failed", err?.message || err);
      setData({ items: [], meta: { page: 1, page_size: 10, total: 0, total_pages: 0 } });
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError("");
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code,
      label: row.label,
      days_offset: row.days_offset,
      priority_weight: row.priority_weight,
      is_active: row.is_active,
    });
    setFormError("");
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        days_offset: Number(form.days_offset),
        priority_weight: Number(form.priority_weight),
      };
      if (editing) {
        const { code, ...update } = payload;
        await api.patch(`${ENDPOINT}/${editing.id}`, update);
      } else {
        await api.post(ENDPOINT, payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(errorToMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    await api.delete(`${ENDPOINT}/${toDelete.id}`);
    await load();
  };

  return (
    <div>
      <PageHeader
        title="Warranty Eligibility Rules"
        subtitle="Configurable expiry windows that drive the AI Warranty renewal queue."
        actions={
          <button
            onClick={openCreate}
            data-testid="btn-new-rule"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm"
          >
            <Plus size={16} /> New Rule
          </button>
        }
      />

      <Toolbar>
        <SearchInput value={q} onChange={setQ} placeholder="Search by code or label…" />
        <StatusFilter value={isActive} onChange={setIsActive} />
      </Toolbar>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="rules-table">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Label</th>
              <th className="text-right px-4 py-2 font-medium">Days Offset</th>
              <th className="text-right px-4 py-2 font-medium">Priority</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500"><Loader2 className="inline animate-spin" size={16}/> Loading…</td></tr>
            ) : data?.items?.length ? (
              data.items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-slate-700">{r.code}</td>
                  <td className="px-4 py-2">{r.label}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.days_offset}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.priority_weight}</td>
                  <td className="px-4 py-2"><StatusBadge active={r.is_active} /></td>
                  <td className="px-4 py-2 text-right">
                    <button data-testid={`edit-${r.code}`} onClick={() => openEdit(r)} className="text-slate-600 hover:text-slate-900 mr-2" title="Edit"><Pencil size={16} /></button>
                    <button data-testid={`delete-${r.code}`} onClick={() => setToDelete(r)} className="text-rose-600 hover:text-rose-800" title="Delete"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6}><EmptyState label="No eligibility rules yet." /></td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination meta={data?.meta} page={page} onPage={setPage} />

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit ${editing.code}` : "New Eligibility Rule"}>
        <form onSubmit={submit} className="space-y-3" data-testid="rule-form">
          <ErrorMessage message={formError} />
          <FormRow label="Code (UPPER_SNAKE)">
            <input
              className="input"
              required
              disabled={!!editing}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              pattern="^[A-Z0-9_]+$"
              data-testid="field-code"
            />
          </FormRow>
          <FormRow label="Label">
            <input
              className="input"
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="field-label"
            />
          </FormRow>
          <FormRow label="Days Offset (negative = before expiry, positive = after)">
            <input
              type="number"
              className="input"
              required
              min={-365}
              max={365}
              value={form.days_offset}
              onChange={(e) => setForm({ ...form, days_offset: e.target.value })}
              data-testid="field-days-offset"
            />
          </FormRow>
          <FormRow label="Priority Weight (higher = called first)">
            <input
              type="number"
              className="input"
              min={0}
              max={10000}
              value={form.priority_weight}
              onChange={(e) => setForm({ ...form, priority_weight: e.target.value })}
              data-testid="field-priority"
            />
          </FormRow>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="field-active"
            />
            Active
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm rounded-md border border-slate-300">Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="rule-submit"
              className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60"
            >
              {submitting ? "Saving…" : editing ? "Save Changes" : "Create Rule"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={doDelete}
        title="Delete eligibility rule?"
        message={`This will soft-delete "${toDelete?.code}". You can recreate it later.`}
        confirmLabel="Delete"
      />
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
